import type { UrlSyncedQueryV2 } from "@/features/analytics/query";
import type {
  FilterV2,
  FrontendFindQuery,
} from "schemas/generated/analyticsApiV2";

import { trpc } from "@editor/utils/trpc";

import { skipToken } from "@tanstack/react-query";
import {
  convertRangeToRangeV2,
  getRangesFromTimePeriod,
} from "replo-utils/analytics";

import { useAnalyticsQueryContextV2 } from "../contexts/AnalyticsQueryContext";

type FrontendFindQueryWithoutNamespaces = Omit<FrontendFindQuery, "namespaces">;

type UseAnalyticsApiV2Props = {
  workspaceId: string | null;
} & (
  | {
      type: "withoutUrlSyncQuery";
      queryConfig: FrontendFindQueryWithoutNamespaces;
    }
  | {
      type: "withUrlSyncQuery";
      queryConfig: {
        useChartInterval: boolean;
        queryOverrides?: Partial<UrlSyncedQueryV2>;
      };
    }
);

const useAnalyticsApiV2 = ({
  workspaceId,
  type,
  queryConfig,
}: UseAnalyticsApiV2Props) => {
  const { query: contextQuery, timeZone } = useAnalyticsQueryContextV2();

  let query: FrontendFindQueryWithoutNamespaces;

  if (type === "withoutUrlSyncQuery") {
    query = queryConfig;
  } else {
    const { useChartInterval, queryOverrides } = queryConfig;

    const { selectedTimePeriod, chartInterval } = contextQuery;

    // Compute the final ranges (with startDatetime/endDatetime/interval) from the fields
    // in the urlSyncedQuery.
    const rangesFromUrlSyncedQuery = getRangesFromTimePeriod({
      selectedTimePeriod,
      chartInterval: useChartInterval ? chartInterval : null,
      timeZone,
    }).updatedRanges;

    const compareRange = rangesFromUrlSyncedQuery.compareAtRanges?.[0];

    const urlHostFilter: FilterV2 = {
      type: "domain",
      operator: "EQ",
      values: contextQuery.urlHosts,
    };

    // The final AnalyticsQuery is a simple combination of the internalQuery and the urlSyncedQuery
    query = {
      ...contextQuery,
      filters: [...contextQuery.filters, urlHostFilter],
      ranges: {
        main: convertRangeToRangeV2(rangesFromUrlSyncedQuery.mainRange),
        compare: compareRange ? convertRangeToRangeV2(compareRange) : undefined,
      },
      ...queryOverrides,
    };
  }

  const shouldFetch = Boolean(workspaceId) && contextQuery.urlHosts.length > 0;

  const findV2 = trpc.analytics.findV2.useQuery(
    shouldFetch ? { query, workspaceId: workspaceId ?? "" } : skipToken,
  );

  const hasDataV2 = trpc.analytics.hasDataV2.useQuery(
    shouldFetch ? { workspaceId: workspaceId ?? "" } : skipToken,
  );

  const refetch = async () =>
    Promise.all([findV2.refetch(), hasDataV2.refetch()]);

  const { main: mainFindResults, compare: compareFindResults } =
    findV2.data ?? {};

  return {
    hasAnyData: Boolean(hasDataV2.data),
    mainFindResults: mainFindResults ?? [],
    totalRowsCount: findV2.data?.totalRows ?? null,
    compareFindResults: compareFindResults ?? [],
    isLoading: findV2.isLoading,
    constructedQuery: query,
    refetch,
  };
};

export default useAnalyticsApiV2;
